import { IResult } from "redmond";

var levenshtein = require("fast-levenshtein");

// Max normalized edit distance (distance / max(len_a, len_b)) accepted as a
// fuzzy match. 0.25 allows ~1 typo per 4 characters while rejecting clearly
// unrelated strings.
const MAX_NORMALIZED_DISTANCE = 0.25;

const findBestLevenshteinMatch = (
  locations: IResult[],
  searchTerm: string
): IResult | undefined => {
  const term = searchTerm.toLowerCase();
  if (term.length === 0) return undefined;

  let best: IResult | undefined;
  let bestScore = Infinity;

  for (const loc of locations) {
    const label = loc.label.toLowerCase();
    const dist = levenshtein.get(term, label);
    const normalized = dist / Math.max(term.length, label.length);
    if (normalized < bestScore) {
      bestScore = normalized;
      best = loc;
    }
  }

  return bestScore <= MAX_NORMALIZED_DISTANCE ? best : undefined;
};

export type LocationMatchType =
  | "exact"
  | "startsWith"
  | "includes"
  | "fuzzy"
  | "none";

export interface LocationMatch {
  result: IResult | undefined;
  matchType: LocationMatchType;
}

export const findMatchingLocation = (
  locations: IResult[],
  searchTerm: string
): LocationMatch => {
  const normalizedTerm = searchTerm.toLowerCase();

  const exact = locations.find((r) => r.label.toLowerCase() === normalizedTerm);
  if (exact) return { result: exact, matchType: "exact" };

  const startsWith = locations.find((r) =>
    r.label.toLowerCase().startsWith(normalizedTerm)
  );
  if (startsWith) return { result: startsWith, matchType: "startsWith" };

  const includes = locations.find((r) =>
    r.label.toLowerCase().includes(normalizedTerm)
  );
  if (includes) return { result: includes, matchType: "includes" };

  const fuzzy = findBestLevenshteinMatch(locations, searchTerm);
  if (fuzzy) return { result: fuzzy, matchType: "fuzzy" };

  return { result: undefined, matchType: "none" };
};
